import { Box, Chip, Typography } from '@mui/material';

import ShortlistsV1Screen from '@/karehero-app/care/shortlists/ShortlistsV1Screen';
import ShortlistView from '@/karehero-app/care/shortlists/ShortlistView';
import ProviderDetail from '@/karehero-app/care/shortlists/ProviderDetail';
import ProviderCard from '@/karehero-app/care/shortlists/ProviderCard';
import ShortlistToast from '@/karehero-app/care/shortlists/ShortlistToast';
import HomeScreen from '@/karehero-app/home/HomeScreen';
import NotificationsScreen from '@/karehero-app/notifications/NotificationsScreen';
import {
  SHORTLISTS,
  providerById,
  shortlistById,
} from '@/karehero-app/care/shortlists/fixtures';
import { shortlistSceneById } from '@/karehero-app/care/shortlists/Shortlists.scenes';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

const RESIDENTIAL = shortlistById('residential-nursing');
const DEMENTIA = shortlistById('dementia-specialists');
const OAKWOOD = providerById('residential-nursing', 'oakwood'); // Secured
const RIVERSIDE = providerById('residential-nursing', 'riverside'); // Contacted
const BRAMBLE = providerById('residential-nursing', 'bramble'); // Unavailable

// Shortlists V1: a navigation variant of the care-shortlists feature. Same
// browse/secure/book flow, different placement — a primary tab instead of a
// Care › Tools pushed stack. Rendered live from the prototype components.
export const careShortlistsV1Spec: HandoverSpec = {
  id: 'care-shortlists-v1',
  title: 'Care search v1',
  description:
    'A navigation variant of care shortlists. Instead of living under Care › Tools as a pushed stack, shortlists become a primary bottom-nav tab (“Search”). The shortlist home is a real tab page with the standard app bar, and provider detail opens as a drawer titled with the care home name. The browse / secure / book flow is unchanged from the base feature. In this version the other tabs and the app-bar bell are deliberately inert, keeping the prototype focused on the care flow.',
  links: [
    {
      label: 'Base feature handover (Care search v2)',
      url: '/handover/care-shortlists',
    },
    {
      label: 'Live flow (Search tab)',
      url: '/karehero-app/shortlists-v1',
    },
    {
      label: 'Mini-PRD (Linear)',
      url: 'https://linear.app/karehero/document/mini-prd-mobile-shortlist-viewing-for-care-circles-35cbcd6463c0',
    },
  ],
  flows: [
    {
      title: 'Browse from the Search tab',
      steps: [
        {
          id: 'search-tab',
          title: 'Search tab',
          tap: 'Tap ‘Search’ in the tab bar',
          render: () => (
            <ShortlistsV1Screen initial={shortlistSceneById('home').initial} />
          ),
        },
        {
          id: 'view',
          title: 'Shortlist view',
          tap: 'Tap ‘Residential with nursing’',
          render: () => (
            <ShortlistsV1Screen initial={shortlistSceneById('view').initial} />
          ),
        },
        {
          id: 'drawer',
          title: 'Provider drawer',
          tap: 'Tap Riverside lodge (Contacted)',
          render: () => (
            <ShortlistsV1Screen
              initial={shortlistSceneById('drawer').initial}
            />
          ),
        },
        {
          id: 'book',
          title: 'Book a visit',
          tap: 'Tap ‘Book a visit’ (secondary)',
          render: () => (
            <ShortlistsV1Screen initial={shortlistSceneById('book').initial} />
          ),
        },
      ],
    },
    {
      title: 'From “shortlist published” to the shortlist',
      steps: [
        {
          id: 'toast',
          title: 'In-app toast',
          tap: 'A shortlist is published while you’re in the app',
          render: () => (
            <Box
              sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}
            >
              <HomeScreen />
              <Box
                sx={{
                  position: 'absolute',
                  top: 96,
                  left: 16,
                  right: 16,
                  zIndex: 20,
                }}
              >
                <ShortlistToast
                  title='New care shortlist ready'
                  body='Yasmin published ‘Residential with nursing’ for Mum'
                />
              </Box>
            </Box>
          ),
        },
        {
          id: 'notification',
          title: 'Notification centre',
          tap: 'Or open it later from the bell',
          render: () => <NotificationsScreen />,
        },
        {
          id: 'search-from-notification',
          title: 'Search tab',
          tap: 'Tap ‘New care shortlist ready’',
          render: () => <ShortlistsV1Screen />,
        },
      ],
    },
  ],
  states: [
    {
      id: 'search-home',
      title: 'Search tab · app bar + locked nav',
      kind: 'happy',
      repro: 'ShortlistsV1Screen — the tab chrome, inert nav and bell',
      render: () => <ShortlistsV1Screen />,
    },
    {
      id: 'view-statuses',
      title: 'All four provider statuses',
      kind: 'happy',
      repro:
        'Residential with nursing: Secured / Contacted / Not contacted / Unavailable',
      render: () => (
        <ShortlistView
          shortlist={RESIDENTIAL}
          onBack={noop}
          onOpenProvider={noop}
          onToggleSecured={noop}
        />
      ),
    },
    {
      id: 'drawer-title',
      title: 'Drawer · secure primary, book secondary',
      kind: 'happy',
      repro:
        'ProviderDetail asSheet — name is the sheet title; footer is Mark as secured (primary) + Book a visit (secondary)',
      render: () => (
        <ProviderDetail
          asSheet
          provider={OAKWOOD}
          onBack={noop}
          onToggleSecured={noop}
        />
      ),
    },
    {
      id: 'home-empty',
      title: 'No shortlists yet',
      kind: 'empty',
      repro:
        'shortlists={[]} — empty content, but the tab bar and app bar still show',
      render: () => <ShortlistsV1Screen shortlists={[]} />,
    },
    {
      id: 'unsecure-revert',
      title: 'Un-securing reverts the status',
      kind: 'edge',
      repro:
        'Marking a secured home as not secured should revert it to its previous status if that’s known; if not, fall back to Contacted. Here: Oakwood (Secured) → tap Mark as not secured.',
      render: () => (
        <ProviderDetail
          asSheet
          provider={OAKWOOD}
          onBack={noop}
          onToggleSecured={noop}
        />
      ),
    },
    {
      id: 'drawer-unavailable',
      title: 'Unavailable provider',
      kind: 'edge',
      repro: 'Bramble court: muted, no Book a visit CTA',
      render: () => <ProviderDetail asSheet provider={BRAMBLE} onBack={noop} />,
    },
    {
      id: 'view-single',
      title: 'Small shortlist',
      kind: 'edge',
      repro: 'Dementia specialists: two providers, same layout',
      render: () => (
        <ShortlistView
          shortlist={DEMENTIA}
          onBack={noop}
          onOpenProvider={noop}
          onToggleSecured={noop}
        />
      ),
    },
    {
      id: 'home-error',
      title: 'Failed to load',
      kind: 'error',
      repro:
        'error — ErrorState in the tab page, tab bar and app bar still show',
      render: () => <ShortlistsV1Screen error />,
    },
  ],
  requirements: [
    {
      text: 'Shortlists is a primary bottom-nav tab labelled “Search” (magnifying-glass icon), active on this screen',
      status: 'built',
      where: 'Search tab',
    },
    {
      text: 'The shortlist home is a real tab page (no Back) with the standard app bar: logo, care recipient name, bell and avatar',
      status: 'built',
      where: 'Search tab',
    },
    {
      text: 'Page heading reads “Care search” with a care-circle-facing description',
      status: 'built',
      where: 'Search tab',
    },
    {
      text: 'Provider detail opens as a drawer (bottom sheet) titled with the care home name; the name is not repeated in the body',
      status: 'built',
      where: 'Provider drawer',
    },
    {
      text: 'From the drawer, Mark as secured is the primary action and Book a visit is secondary; securing toggles (Mark as not secured when already secured); unavailable homes show neither',
      status: 'built',
      where: 'Provider drawer',
    },
    {
      text: 'Tappable cards have a press state (slight scale-down, no shadow)',
      status: 'built',
      where: 'Shortlist home + shortlist view',
    },
    {
      text: 'Navigation is locked to the care flow: the other tabs and the app-bar bell are inert (prototype scoping, not a shipped behaviour)',
      status: 'built',
      where: 'Search tab',
      note: 'Deliberate for user testing; the real build would wire these up',
    },
    {
      text: 'Browse / secure / contact / book behaviour is unchanged from the base care-shortlists feature',
      status: 'built',
      where: 'See the base handover',
      note: 'Same ShortlistView, ProviderDetail and ProviderCard',
    },
    {
      text: 'Notification when a shortlist is published, deep-linking into the Search tab',
      status: 'built',
      where: 'Notification centre',
      note: 'An in-app toast announces it, and a “New care shortlist ready” notification sits in the centre; tapping it deep-links to /karehero-app/shortlists-v1. The server-side publish trigger and channel (push/email/in-app) are out of scope here — an open PRD question',
    },
  ],
  components: {
    mui: ['Chip', 'Typography'],
    existing: [
      'ShortlistHome',
      'ShortlistView',
      'ProviderDetail',
      'ProviderCard',
      'TabBar',
      'BottomSheet',
      'PushScreen',
      'Card',
      'NotificationsScreen',
    ],
    new: ['ShortlistsV1Screen', 'ShortlistToast'],
  },
  componentsNote:
    'Reuses the base care-shortlists components. New here: the ShortlistsV1Screen shell and the ShortlistToast banner; TabBar gained an items prop, ShortlistHome gained appBar / heading / description slots, and ProviderDetail gained the onToggleSecured drawer action.',
  componentPreviews: {
    Chip: () => <Chip label='Contacted' size='small' />,
    Typography: () => <Typography variant='h4'>Care search</Typography>,
    ProviderCard: () => (
      <ProviderCard provider={RIVERSIDE} onOpen={noop} onToggleSecured={noop} />
    ),
    ShortlistToast: () => (
      <ShortlistToast
        title='New care shortlist ready'
        body='Yasmin published ‘Residential with nursing’ for Mum'
      />
    ),
  },
};
